/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import {
    Button, Input, Modal, message, Tag, Select, Space,
    Popconfirm, Tooltip, Row, Col, DatePicker
} from 'antd';
import {
    PlusOutlined, EditOutlined, DeleteOutlined,
    CheckOutlined, SearchOutlined, FileTextOutlined,
    ClockCircleOutlined, CheckCircleOutlined, WarningOutlined, DashboardOutlined
} from '@ant-design/icons';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
    ResponsiveContainer, PieChart, Pie, Cell, Legend, LabelList
} from 'recharts';
import moment from 'moment';
import FleetService from '../../services/FleetService';

const { TextArea } = Input;
const { Option }   = Select;

// ─── Constants ───────────────────────────────────────────────────────────────
// ─── SHARED: Period Filter Component ────────────────────────────────────────
// Drop this into any file that needs time period filtering
// Usage: <PeriodFilter value={period} onChange={setPeriod} />
// period = { preset: 'this_month'|'last_3m'|'this_year'|'last_year'|'custom'|'all', from: 'YYYY-MM-DD', to: 'YYYY-MM-DD' }
// Filter data: filterByPeriod(data, period, dateField)

const { RangePicker } = DatePicker;

const PRESETS = [
    { key: 'this_month', label: 'This Month',   from: () => moment().startOf('month'),   to: () => moment() },
    { key: 'last_3m',    label: 'Last 3 Months',from: () => moment().subtract(3,'months'),to:() => moment() },
    { key: 'this_year',  label: 'This Year',     from: () => moment().startOf('year'),    to: () => moment() },
    { key: 'last_year',  label: 'Last Year',     from: () => moment().subtract(1,'year').startOf('year'), to: () => moment().subtract(1,'year').endOf('year') },
    { key: 'all',        label: 'All Time',      from: () => null, to: () => null },
];

const DEFAULT_PERIOD = { preset: 'this_year', from: moment().startOf('year').format('YYYY-MM-DD'), to: moment().format('YYYY-MM-DD') };

function PeriodFilter({ value, onChange, style }) {
    const cur = value || DEFAULT_PERIOD;
    const handlePreset = (preset) => {
        const p = PRESETS.find(x => x.key === preset);
        onChange({
            preset,
            from: p.from() ? p.from().format('YYYY-MM-DD') : '',
            to:   p.to()   ? p.to().format('YYYY-MM-DD')   : '',
        });
    };
    const handleRange = ([s, e]) => {
        onChange({
            preset: 'custom',
            from: s ? s.format('YYYY-MM-DD') : '',
            to:   e ? e.format('YYYY-MM-DD') : '',
        });
    };
    return (
        <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap', ...style }}>
            <span style={{ fontSize:11, color:'#888', whiteSpace:'nowrap' }}>Period:</span>
            {PRESETS.map(p => (
                <button key={p.key} onClick={() => handlePreset(p.key)} style={{
                    padding:'3px 10px', border:'1px solid', borderRadius:12, cursor:'pointer',
                    fontSize:11, fontWeight: cur.preset===p.key ? 600 : 400,
                    background: cur.preset===p.key ? '#1a3a5c' : '#fff',
                    borderColor: cur.preset===p.key ? '#1a3a5c' : '#d9d9d9',
                    color: cur.preset===p.key ? '#fff' : '#595959',
                }}>{p.label}</button>
            ))}
            <RangePicker size="small" style={{ width:210 }}
                value={cur.preset==='custom' && cur.from && cur.to
                    ? [moment(cur.from), moment(cur.to)] : null}
                onChange={handleRange}
                format="DD MMM YYYY"
                placeholder={['Custom from','Custom to']}
            />
            {cur.from && cur.to && (
                <span style={{ fontSize:10, color:'#1a5b9a', background:'#e6f4ff',
                    padding:'2px 8px', borderRadius:8, whiteSpace:'nowrap' }}>
                    {moment(cur.from).format('DD MMM YYYY')} → {moment(cur.to).format('DD MMM YYYY')}
                </span>
            )}
        </div>
    );
}

function filterByPeriod(data, period, dateField) {
    if (!period || period.preset === 'all' || (!period.from && !period.to)) return data;
    return data.filter(r => {
        const d = r[dateField];
        if (!d || d === '0000-00-00') return false;
        const m = moment(d);
        if (period.from && m.isBefore(moment(period.from), 'day')) return false;
        if (period.to   && m.isAfter(moment(period.to),   'day')) return false;
        return true;
    });
}

const DISPOSAL_METHODS  = ['Auction', 'Trade-In', 'Scrap / Crush', 'Donation', 'Transfer to Another Ministry', 'Write-Off', 'Other'];
const DISPOSAL_REASONS  = ['Beyond Economical Repair', 'End of Useful Life', 'Accident Damage', 'Theft', 'Obsolete Technology', 'Replaced by New Vehicle', 'Other'];
const CONDITIONS        = ['Good', 'Fair', 'Poor', 'Very Poor', 'Unroadworthy', 'Scrap'];

// Stage pipeline
const STAGES = [
    { key: 'Pending',           label: 'Pending',            color: 'default',  icon: <ClockCircleOutlined /> },
    { key: 'HOD Approved',      label: 'HOD Approved',       color: 'blue',     icon: <CheckCircleOutlined /> },
    { key: 'Director Approved', label: 'Director Approved',  color: 'purple',   icon: <CheckCircleOutlined /> },
    { key: 'Disposed',          label: 'Disposed',           color: 'success',  icon: <CheckCircleOutlined /> },
];

const STAGE_IDX = Object.fromEntries(STAGES.map((s, i) => [s.key, i]));

const fmt  = (v) => v || '—';
const fmtD = (v) => v ? moment(v).format('DD MMM YYYY') : '—';


// ─── Anomaly detector ────────────────────────────────────────────────────────
function detectDisposalAnomalies(disposals) {
    const issues = [];
    // Pending > 90 days
    disposals.filter(d => d.status === 'Pending').forEach(d => {
        const days = moment().diff(moment(d.created||'2020-01-01'), 'days');
        if (days > 90) issues.push({ type:'warn', msg:`${d.registration_no} — Pending for ${days} days with no HOD action` });
    });
    // HOD approved > 60 days without director approval
    disposals.filter(d => d.status === 'HOD Approved').forEach(d => {
        const days = d.date_hod_approved ? moment().diff(moment(d.date_hod_approved), 'days') : 0;
        if (days > 60) issues.push({ type:'warn', msg:`${d.registration_no} — HOD approved ${days} days ago, awaiting Director` });
    });
    // No sale price set but disposal method requires it
    disposals.filter(d => ['Auction','Trade-In'].includes(d.disposal_method) && !d.sale_price)
        .forEach(d => issues.push({ type:'error', msg:`${d.registration_no} — ${d.disposal_method} method but no sale price recorded` }));
    // No board authority reference
    disposals.filter(d => d.status !== 'Pending' && !d.board_authority_ref)
        .forEach(d => issues.push({ type:'warn', msg:`${d.registration_no} — Approved but missing Board Authority Reference` }));
    return issues;
}

// ─── Disposal Mini Dashboard ──────────────────────────────────────────────────
function DisposalDashboard({ disposals, period, onPeriodChange }) {
    const toN = (v) => Number(v||0);

    const byReason = useMemo(() => {
        const m = {};
        disposals.forEach(d => { m[d.reason_for_disposal||'Unknown'] = (m[d.reason_for_disposal||'Unknown']||0)+1; });
        return Object.entries(m).map(([name,value]) => ({name,value})).sort((a,b)=>b.value-a.value);
    }, [disposals]);

    const byMethod = useMemo(() => {
        const m = {};
        disposals.forEach(d => { m[d.disposal_method||'Not Set'] = (m[d.disposal_method||'Not Set']||0)+1; });
        return Object.entries(m).map(([name,value]) => ({name,value}));
    }, [disposals]);

    const byStage = useMemo(() => [
        { name:'Pending',           count: disposals.filter(d=>d.status==='Pending').length           },
        { name:'HOD Approved',      count: disposals.filter(d=>d.status==='HOD Approved').length      },
        { name:'Director Approved', count: disposals.filter(d=>d.status==='Director Approved').length },
        { name:'Disposed',          count: disposals.filter(d=>d.status==='Disposed').length          },
    ], [disposals]);

    const totalProceeds  = disposals.reduce((s,d) => s + toN(d.proceeds_to_govt), 0);
    const totalSalePrice = disposals.reduce((s,d) => s + toN(d.sale_price), 0);
    const STAGE_C = ['#faad14','#1d4ed8','#6d28d9','#52c41a'];
    const PIE_C   = ['#1890ff','#52c41a','#faad14','#f5222d','#722ed1','#13c2c2'];

    const anomalies = useMemo(() => detectDisposalAnomalies(disposals), [disposals]);

    return (
        <div style={{ background:'#fafafa', border:'1px solid #e8e8e8', borderRadius:8,
            padding:'14px 16px', marginBottom:20 }}>
            {/* Period filter */}
            <PeriodFilter value={period} onChange={onPeriodChange} style={{ marginBottom:12 }} />
            {disposals.length === 0 && (
                <div style={{ textAlign:'center', padding:16, color:'#aaa', fontSize:12 }}>
                    No disposal records in selected period
                </div>
            )}

            {/* Anomaly banner */}
            {anomalies.length > 0 && (
                <div style={{ background:'#fff1f0', border:'1px solid #ffa39e', borderRadius:6,
                    padding:'8px 14px', marginBottom:14 }}>
                    <div style={{ fontWeight:700, color:'#cf1322', fontSize:12, marginBottom:6 }}>
                        <WarningOutlined style={{ marginRight:6 }} />
                        {anomalies.length} irregularit{anomalies.length>1?'ies':'y'} detected
                    </div>
                    {anomalies.map((a,i) => (
                        <div key={i} style={{ fontSize:11, display:'flex', gap:6, marginBottom:3 }}>
                            <Tag color={a.type==='error'?'red':'orange'} style={{ fontSize:9 }}>
                                {a.type==='error'?'Error':'Warning'}
                            </Tag>
                            {a.msg}
                        </div>
                    ))}
                </div>
            )}

            {/* Insight pills */}
            <div style={{ display:'flex', gap:10, marginBottom:14, flexWrap:'wrap' }}>
                {[
                    { label:'Total Sale Value',   value:`E ${totalSalePrice.toLocaleString()}`, color:'#d46b08' },
                    { label:'Govt. Proceeds',     value:`E ${totalProceeds.toLocaleString()}`,  color:'#389e0d' },
                    { label:'Awaiting Action',    value: disposals.filter(d=>d.status!=='Disposed').length, color:'#1890ff' },
                    { label:'No Board Auth. Ref', value: disposals.filter(d=>!d.board_authority_ref).length, color: disposals.filter(d=>!d.board_authority_ref).length>0?'#cf1322':'#52c41a' },
                ].map(({label,value,color}) => (
                    <div key={label} style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:6,
                        padding:'6px 12px', borderLeft:`3px solid ${color}` }}>
                        <div style={{ fontSize:10, color:'#888' }}>{label}</div>
                        <div style={{ fontSize:16, fontWeight:700, color }}>{value}</div>
                    </div>
                ))}
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
                {/* Pipeline stage bar */}
                <div style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:6, padding:'10px' }}>
                    <div style={{ fontSize:11, fontWeight:600, marginBottom:6, color:'#1a3a5c' }}>By Approval Stage</div>
                    <ResponsiveContainer width="100%" height={150}>
                        <BarChart data={byStage} margin={{ top:0, right:5, left:-15, bottom:30 }}>
                            <XAxis dataKey="name" tick={{ fontSize:8 }} angle={-20} textAnchor="end" />
                            <YAxis tick={{ fontSize:9 }} allowDecimals={false} />
                            <RTooltip />
                            <Bar dataKey="count" radius={[4,4,0,0]}>
                                {byStage.map((_,i) => <Cell key={i} fill={STAGE_C[i]} />)}
                                <LabelList dataKey="count" position="top" style={{ fontSize:10 }} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {/* Reason pie */}
                <div style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:6, padding:'10px' }}>
                    <div style={{ fontSize:11, fontWeight:600, marginBottom:6, color:'#1a3a5c' }}>By Disposal Reason</div>
                    <ResponsiveContainer width="100%" height={150}>
                        <PieChart>
                            <Pie data={byReason} dataKey="value" nameKey="name"
                                cx="50%" cy="50%" outerRadius={60} labelLine={false}>
                                {byReason.map((_,i) => <Cell key={i} fill={PIE_C[i%PIE_C.length]} />)}
                            </Pie>
                            <RTooltip />
                            <Legend iconSize={8} wrapperStyle={{ fontSize:9 }} />
                        </PieChart>
                    </ResponsiveContainer>
                </div>

                {/* Method pie */}
                <div style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:6, padding:'10px' }}>
                    <div style={{ fontSize:11, fontWeight:600, marginBottom:6, color:'#1a3a5c' }}>By Disposal Method</div>
                    <ResponsiveContainer width="100%" height={150}>
                        <PieChart>
                            <Pie data={byMethod} dataKey="value" nameKey="name"
                                cx="50%" cy="50%" outerRadius={60}
                                label={({name,percent}) => `${(percent*100).toFixed(0)}%`}>
                                {byMethod.map((_,i) => <Cell key={i} fill={PIE_C[i%PIE_C.length]} />)}
                            </Pie>
                            <RTooltip />
                            <Legend iconSize={8} wrapperStyle={{ fontSize:9 }} />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
}

// ─── Approval pipeline progress bar ──────────────────────────────────────────
function Pipeline({ status }) {
    const cur = STAGE_IDX[status] ?? 0;
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginTop: 4 }}>
            {STAGES.map((s, i) => {
                const done    = i <= cur;
                const current = i === cur;
                return (
                    <React.Fragment key={s.key}>
                        <div style={{
                            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
                        }}>
                            <div style={{
                                width: 28, height: 28, borderRadius: '50%',
                                background: done ? (current ? '#722ed1' : '#52c41a') : '#e8e8e8',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: done ? '#fff' : '#aaa', fontSize: 12,
                                border: current ? '2px solid #722ed1' : 'none',
                                fontWeight: 700,
                            }}>{i + 1}</div>
                            <div style={{ fontSize: 9, color: done ? '#333' : '#aaa', whiteSpace: 'nowrap', maxWidth: 60, textAlign: 'center' }}>
                                {s.label}
                            </div>
                        </div>
                        {i < STAGES.length - 1 && (
                            <div style={{
                                flex: 1, height: 3, minWidth: 20,
                                background: i < cur ? '#52c41a' : '#e8e8e8',
                                marginBottom: 16,
                            }} />
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
}

// ─── Disposal Form Modal ──────────────────────────────────────────────────────
function DisposalFormModal({ open, disposal, vehicles, onSaved, onClose }) {
    const [form, setForm]     = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setForm(disposal ? { ...disposal } : {
                id: null, vehicle_id: null, reason_for_disposal: '',
                condition_at_disposal: '', mileage_at_disposal: '',
                board_authority_ref: '', minutes_of_meeting_ref: '',
                disposal_method: '', sale_price: '', proceeds_to_govt: '',
                replacement_vehicle_reg: '', receiving_entity: '', notes: '',
            });
        }
    }, [open, disposal?.id]);

    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

    const handleSave = async () => {
        if (!form.vehicle_id)          { message.error('Select a vehicle');        return; }
        if (!form.reason_for_disposal) { message.error('Select a disposal reason'); return; }
        setSaving(true);
        try {
            const res = await FleetService.saveDisposal(form);
            if (Number(res?.data?.code) === 200) {
                message.success(res.data.message || 'Disposal saved');
                onSaved();
            } else { message.error(res?.data?.message || 'Failed to save'); }
        } catch (e) { message.error(e.message || 'Error'); }
        finally { setSaving(false); }
    };

    const lbl  = { fontSize: 12, color: '#666', marginBottom: 4 };
    const fbox = { marginBottom: 14 };

    return (
        <Modal open={open} onCancel={onClose} width={720} footer={null} destroyOnClose
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <DeleteOutlined style={{ color: '#cf1322' }} />
                    <span>{form.id ? 'Edit Disposal Record' : 'Register Vehicle for Disposal'}</span>
                </div>
            }
        >
            {/* Section 1 — Vehicle */}
            <div style={{ background: '#fff1f0', border: '1px solid #ffa39e', borderRadius: 6, padding: '14px 16px', marginBottom: 14 }}>
                <div style={{ fontWeight: 600, color: '#cf1322', marginBottom: 12 }}>🚗 Vehicle Details</div>
                <Row gutter={16}>
                    <Col span={24}>
                        <div style={fbox}>
                            <div style={lbl}>Vehicle <span style={{ color: 'red' }}>*</span></div>
                            <Select showSearch style={{ width: '100%' }} value={form.vehicle_id}
                                onChange={v => set('vehicle_id', v)}
                                placeholder="Search by registration plate..."
                                optionFilterProp="children"
                                disabled={!!form.id}
                            >
                                {vehicles.map(v => (
                                    <Option key={v.id} value={v.id}>
                                        {v.registration_no} — {v.vehicle_type} ({v.facility})
                                    </Option>
                                ))}
                            </Select>
                        </div>
                    </Col>
                    <Col span={12}>
                        <div style={fbox}>
                            <div style={lbl}>Condition at Disposal</div>
                            <Select style={{ width: '100%' }} value={form.condition_at_disposal}
                                onChange={v => set('condition_at_disposal', v)} allowClear placeholder="Select condition">
                                {CONDITIONS.map(c => <Option key={c} value={c}>{c}</Option>)}
                            </Select>
                        </div>
                    </Col>
                    <Col span={12}>
                        <div style={fbox}>
                            <div style={lbl}>Mileage at Disposal (km)</div>
                            <Input type="number" value={form.mileage_at_disposal}
                                onChange={e => set('mileage_at_disposal', e.target.value)}
                                placeholder="Current odometer reading" />
                        </div>
                    </Col>
                </Row>
            </div>

            {/* Section 2 — Reason & Method */}
            <div style={{ background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 6, padding: '14px 16px', marginBottom: 14 }}>
                <div style={{ fontWeight: 600, color: '#d46b08', marginBottom: 12 }}>📋 Disposal Details</div>
                <Row gutter={16}>
                    <Col span={12}>
                        <div style={fbox}>
                            <div style={lbl}>Reason for Disposal <span style={{ color: 'red' }}>*</span></div>
                            <Select style={{ width: '100%' }} value={form.reason_for_disposal}
                                onChange={v => set('reason_for_disposal', v)} placeholder="Select reason">
                                {DISPOSAL_REASONS.map(r => <Option key={r} value={r}>{r}</Option>)}
                            </Select>
                        </div>
                    </Col>
                    <Col span={12}>
                        <div style={fbox}>
                            <div style={lbl}>Disposal Method</div>
                            <Select style={{ width: '100%' }} value={form.disposal_method}
                                onChange={v => set('disposal_method', v)} placeholder="Select method" allowClear>
                                {DISPOSAL_METHODS.map(m => <Option key={m} value={m}>{m}</Option>)}
                            </Select>
                        </div>
                    </Col>
                    <Col span={12}>
                        <div style={fbox}>
                            <div style={lbl}>Board Authority Ref.</div>
                            <Input value={form.board_authority_ref}
                                onChange={e => set('board_authority_ref', e.target.value)}
                                placeholder="e.g. MOH/BOARD/2026/001" />
                        </div>
                    </Col>
                    <Col span={12}>
                        <div style={fbox}>
                            <div style={lbl}>Minutes of Meeting Ref.</div>
                            <Input value={form.minutes_of_meeting_ref}
                                onChange={e => set('minutes_of_meeting_ref', e.target.value)}
                                placeholder="Meeting minutes reference" />
                        </div>
                    </Col>
                    <Col span={24}>
                        <div style={fbox}>
                            <div style={lbl}>Notes / Remarks</div>
                            <TextArea rows={2} value={form.notes}
                                onChange={e => set('notes', e.target.value)}
                                placeholder="Any additional information..." />
                        </div>
                    </Col>
                </Row>
            </div>

            {/* Section 3 — Financials */}
            <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6, padding: '14px 16px', marginBottom: 20 }}>
                <div style={{ fontWeight: 600, color: '#389e0d', marginBottom: 12 }}>💰 Financial Details</div>
                <Row gutter={16}>
                    <Col span={8}>
                        <div style={fbox}>
                            <div style={lbl}>Sale / Disposal Price (E)</div>
                            <Input type="number" value={form.sale_price}
                                onChange={e => set('sale_price', e.target.value)} placeholder="0.00" />
                        </div>
                    </Col>
                    <Col span={8}>
                        <div style={fbox}>
                            <div style={lbl}>Proceeds to Government (E)</div>
                            <Input type="number" value={form.proceeds_to_govt}
                                onChange={e => set('proceeds_to_govt', e.target.value)} placeholder="0.00" />
                        </div>
                    </Col>
                    <Col span={8}>
                        <div style={fbox}>
                            <div style={lbl}>Replacement Vehicle Reg.</div>
                            <Input value={form.replacement_vehicle_reg}
                                onChange={e => set('replacement_vehicle_reg', e.target.value)}
                                placeholder="New vehicle plate (if any)" />
                        </div>
                    </Col>
                    <Col span={12}>
                        <div style={fbox}>
                            <div style={lbl}>Receiving Entity / Buyer</div>
                            <Input value={form.receiving_entity}
                                onChange={e => set('receiving_entity', e.target.value)}
                                placeholder="Who receives the vehicle" />
                        </div>
                    </Col>
                </Row>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button type="primary" loading={saving} onClick={handleSave}
                    style={{ background: '#cf1322', borderColor: '#cf1322' }}>
                    {form.id ? 'Update Record' : 'Register for Disposal'}
                </Button>
            </div>
        </Modal>
    );
}

// ─── Detail Modal ─────────────────────────────────────────────────────────────
function DisposalDetail({ disposal, onClose, onEdit, onApproveHOD, onApproveDirector, onMarkDisposed, isAdmin }) {
    if (!disposal) return null;
    const stageIdx = STAGE_IDX[disposal.status] ?? 0;

    const row = (label, value) => (
        <div style={{ display: 'flex', padding: '6px 0', borderBottom: '1px solid #f5f5f5' }}>
            <span style={{ width: 190, fontSize: 12, color: '#888', flexShrink: 0 }}>{label}</span>
            <span style={{ fontSize: 12, fontWeight: 500 }}>{value || '—'}</span>
        </div>
    );

    return (
        <Modal open={!!disposal} onCancel={onClose} width={640} destroyOnClose
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <FileTextOutlined />
                    <span>Disposal Record — {disposal.registration_no}</span>
                    <Tag color={STAGES[stageIdx]?.color || 'default'}>{disposal.status}</Tag>
                </div>
            }
            footer={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', gap: 8 }}>
                        {/* HOD approval */}
                        {stageIdx === 0 && isAdmin && (
                            <Popconfirm title="Confirm HOD approval for this disposal?"
                                onConfirm={() => { onApproveHOD(disposal.id); onClose(); }}
                                okText="Approve" okButtonProps={{ style: { background: '#1d4ed8' } }}>
                                <Button type="primary" icon={<CheckOutlined />}
                                    style={{ background: '#1d4ed8', borderColor: '#1d4ed8' }}>
                                    HOD Approve
                                </Button>
                            </Popconfirm>
                        )}
                        {/* Director approval */}
                        {stageIdx === 1 && isAdmin && (
                            <Popconfirm title="Confirm Director approval for this disposal?"
                                onConfirm={() => { onApproveDirector(disposal.id); onClose(); }}
                                okText="Approve" okButtonProps={{ style: { background: '#6d28d9' } }}>
                                <Button type="primary" icon={<CheckOutlined />}
                                    style={{ background: '#6d28d9', borderColor: '#6d28d9' }}>
                                    Director Approve
                                </Button>
                            </Popconfirm>
                        )}
                        {/* Mark disposed */}
                        {stageIdx === 2 && isAdmin && (
                            <Popconfirm title="Mark this vehicle as physically disposed?"
                                onConfirm={() => { onMarkDisposed(disposal.id); onClose(); }}
                                okText="Confirm" okButtonProps={{ danger: true }}>
                                <Button danger icon={<CheckOutlined />}>Mark as Disposed</Button>
                            </Popconfirm>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        {stageIdx === 0 && (
                            <Button icon={<EditOutlined />} onClick={() => { onClose(); onEdit(disposal); }}>Edit</Button>
                        )}
                        <Button onClick={onClose}>Close</Button>
                    </div>
                </div>
            }
        >
            {/* Pipeline */}
            <div style={{ background: '#fafafa', border: '1px solid #e8e8e8', borderRadius: 6, padding: '12px 16px', marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 8 }}>APPROVAL PIPELINE</div>
                <Pipeline status={disposal.status} />
            </div>

            {row('Registration No.',    disposal.registration_no)}
            {row('Vehicle Type',        disposal.vehicle_type)}
            {row('Manufacturer',        disposal.manufacturer)}
            {row('Facility',            disposal.facility)}
            {row('Functional Status',   disposal.functional_status)}
            {row('Year of Manufacture', disposal.year_of_manufacture)}
            {row('Mileage at Disposal', disposal.mileage_at_disposal ? `${Number(disposal.mileage_at_disposal).toLocaleString()} km` : null)}
            {row('Condition',           disposal.condition_at_disposal)}
            {row('Reason for Disposal', disposal.reason_for_disposal)}
            {row('Disposal Method',     disposal.disposal_method)}
            {row('Board Authority Ref', disposal.board_authority_ref)}
            {row('Minutes Ref.',        disposal.minutes_of_meeting_ref)}
            {row('Sale Price',          disposal.sale_price ? `E ${Number(disposal.sale_price).toLocaleString()}` : null)}
            {row('Proceeds to Govt.',   disposal.proceeds_to_govt ? `E ${Number(disposal.proceeds_to_govt).toLocaleString()}` : null)}
            {row('Replacement Vehicle', disposal.replacement_vehicle_reg)}
            {row('Receiving Entity',    disposal.receiving_entity)}
            {row('HOD Approved By',     disposal.hod_approver_name)}
            {row('HOD Approval Date',   fmtD(disposal.date_hod_approved))}
            {row('Director Approved By',disposal.director_approver_name)}
            {row('Director Appr. Date', fmtD(disposal.date_director_approved))}
            {row('Date Disposed',       fmtD(disposal.date_actual_disposal))}
            {disposal.notes && row('Notes', disposal.notes)}
        </Modal>
    );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function FleetDisposals() {
    const [disposals,  setDisposals]  = useState([]);
    const [vehicles,   setVehicles]   = useState([]);
    const [loading,    setLoading]    = useState(false);
    const [showDash,   setShowDash]   = useState(true);
    const [period,     setPeriod]     = useState(DEFAULT_PERIOD);
    const [formOpen,   setFormOpen]   = useState(false);
    const [editing,    setEditing]    = useState(null);
    const [detailItem, setDetailItem] = useState(null);
    const [search,     setSearch]     = useState('');
    const [filterStatus, setFilterStatus] = useState('');

    // Detect admin — role_id === 1
    const isAdmin = (() => {
        try { return JSON.parse(localStorage.getItem('user') || '{}').role_id === 1; } catch { return false; }
    })();

    const load = async () => {
        setLoading(true);
        try {
            const [dRes, vRes] = await Promise.all([
                FleetService.getDisposals(),
                FleetService.getVehiclesList(),
            ]);
            if (Number(dRes?.data?.code) === 200) setDisposals(dRes.data.result || []);
            if (Number(vRes?.data?.code) === 200) setVehicles(vRes.data.result || []);
        } catch { message.error('Failed to load disposal records'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const handleApproveHOD = async (id) => {
        try {
            const res = await FleetService.approveDisposalHOD(id);
            if (Number(res?.data?.code) === 200) { message.success('HOD approval recorded'); load(); }
            else message.error(res?.data?.message || 'Failed');
        } catch { message.error('Failed to approve'); }
    };

    const handleApproveDirector = async (id) => {
        try {
            const res = await FleetService.approveDisposalDirector(id);
            if (Number(res?.data?.code) === 200) { message.success('Director approval recorded'); load(); }
            else message.error(res?.data?.message || 'Failed');
        } catch { message.error('Failed to approve'); }
    };

    const handleMarkDisposed = async (id) => {
        try {
            const res = await FleetService.markDisposed(id);
            if (Number(res?.data?.code) === 200) { message.success('Vehicle marked as disposed'); load(); }
            else message.error(res?.data?.message || 'Failed');
        } catch { message.error('Failed'); }
    };

    // Stats
    const stats = {
        total:    disposals.length,
        pending:  disposals.filter(d => d.status === 'Pending').length,
        hod:      disposals.filter(d => d.status === 'HOD Approved').length,
        director: disposals.filter(d => d.status === 'Director Approved').length,
        disposed: disposals.filter(d => d.status === 'Disposed').length,
    };

    // Filter
    const filtered = disposals.filter(d => {
        const q = search.toLowerCase();
        const matchSearch = !q ||
            (d.registration_no || '').toLowerCase().includes(q) ||
            (d.vehicle_type    || '').toLowerCase().includes(q) ||
            (d.facility        || '').toLowerCase().includes(q) ||
            (d.reason_for_disposal || '').toLowerCase().includes(q);
        const matchStatus = !filterStatus || d.status === filterStatus;
        return matchSearch && matchStatus;
    });

    // Stage badge colours
    const stageBg = { Pending: '#fff7e6', 'HOD Approved': '#e6f4ff', 'Director Approved': '#f9f0ff', Disposed: '#f6ffed' };
    const stageFg = { Pending: '#d46b08', 'HOD Approved': '#1d4ed8', 'Director Approved': '#6d28d9', Disposed: '#389e0d' };

    return (
        <div className="page-content">
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div>
                    <h4 style={{ margin: 0, fontWeight: 700 }}>Vehicle Disposal Register</h4>
                    <div style={{ fontSize: 13, color: '#888', marginTop: 2 }}>
                        4-stage approval pipeline: Pending → HOD Approved → Director Approved → Disposed
                    </div>
                </div>
                <div style={{ display:'flex', gap:8 }}>
                    <Button icon={<DashboardOutlined />}
                        type={showDash?'primary':'default'} ghost={showDash}
                        onClick={() => setShowDash(p=>!p)}>
                        {showDash ? 'Hide Dashboard' : 'Show Dashboard'}
                    </Button>
                    <Button type="primary" icon={<PlusOutlined />}
                        onClick={() => { setEditing(null); setFormOpen(true); }}
                        style={{ background: '#cf1322', borderColor: '#cf1322' }}>
                        Register for Disposal
                    </Button>
                </div>
            </div>

            {/* KPI Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 20 }}>
                {[
                    { label: 'Total Records',       value: stats.total,    color: '#595959' },
                    { label: 'Pending',             value: stats.pending,  color: '#d46b08' },
                    { label: 'HOD Approved',        value: stats.hod,      color: '#1d4ed8' },
                    { label: 'Director Approved',   value: stats.director, color: '#6d28d9' },
                    { label: 'Disposed',            value: stats.disposed, color: '#389e0d' },
                ].map(({ label, value, color }) => (
                    <div key={label} style={{
                        background: '#fff', border: '1px solid #e8e8e8',
                        borderRadius: 8, padding: '14px 16px',
                        borderTop: `3px solid ${color}`,
                    }}>
                        <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>{label}</div>
                        <div style={{ fontSize: 26, fontWeight: 700, color }}>{value}</div>
                    </div>
                ))}
            </div>

            {/* Mini Dashboard */}
            {showDash && disposals.length > 0 && (
                <DisposalDashboard disposals={filterByPeriod(disposals, period, 'created')} period={period} onPeriodChange={setPeriod} />
            )}

            {/* Filters */}
            <div style={{
                display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap',
                background: '#fafafa', border: '1px solid #e8e8e8', borderRadius: 6, padding: '12px 14px',
            }}>
                <Input prefix={<SearchOutlined style={{ color: '#ccc' }} />}
                    placeholder="Search vehicle, type, facility, reason…"
                    value={search} onChange={e => setSearch(e.target.value)}
                    allowClear style={{ width: 280 }} />
                <Select placeholder="Filter by stage" value={filterStatus || undefined}
                    onChange={setFilterStatus} allowClear style={{ width: 180 }}>
                    {STAGES.map(s => <Option key={s.key} value={s.key}>{s.label}</Option>)}
                </Select>
                <div style={{ marginLeft: 'auto', fontSize: 12, color: '#888', alignSelf: 'center' }}>
                    {filtered.length} of {disposals.length} records
                </div>
            </div>

            {/* Table */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: 60, color: '#aaa' }}>Loading disposal records…</div>
            ) : filtered.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 60, color: '#aaa', border: '2px dashed #e8e8e8', borderRadius: 8 }}>
                    <DeleteOutlined style={{ fontSize: 40, marginBottom: 12, display: 'block' }} />
                    {disposals.length === 0
                        ? 'No disposal records yet. Click "Register for Disposal" to begin.'
                        : 'No records match your filters.'}
                </div>
            ) : (
                <div style={{ border: '1px solid #e8e8e8', borderRadius: 8, overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                        <thead>
                            <tr style={{ background: '#1a3a5c', color: '#fff' }}>
                                {['Reg. Plate', 'Vehicle Type', 'Facility', 'Reason', 'Method', 'Mileage', 'Sale Price', 'Stage', 'Pipeline', 'Actions'].map(h => (
                                    <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((d, idx) => {
                                const stageIdx = STAGE_IDX[d.status] ?? 0;
                                return (
                                    <tr key={d.id}
                                        style={{ background: idx % 2 === 0 ? '#fff' : '#f9f9f9', borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }}
                                        onClick={() => setDetailItem(d)}
                                    >
                                        <td style={{ padding: '10px 12px' }}>
                                            <div style={{ fontWeight: 700, color: '#1a5b9a' }}>{fmt(d.registration_no)}</div>
                                            <div style={{ fontSize: 11, color: '#aaa' }}>{d.year_of_manufacture || ''}</div>
                                        </td>
                                        <td style={{ padding: '10px 12px' }}>{fmt(d.vehicle_type)}</td>
                                        <td style={{ padding: '10px 12px', maxWidth: 140 }}>
                                            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {fmt(d.facility)}
                                            </div>
                                        </td>
                                        <td style={{ padding: '10px 12px', maxWidth: 140 }}>
                                            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {fmt(d.reason_for_disposal)}
                                            </div>
                                        </td>
                                        <td style={{ padding: '10px 12px' }}>{fmt(d.disposal_method)}</td>
                                        <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                                            {d.mileage_at_disposal ? Number(d.mileage_at_disposal).toLocaleString() + ' km' : '—'}
                                        </td>
                                        <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                                            {d.sale_price ? `E ${Number(d.sale_price).toLocaleString()}` : '—'}
                                        </td>
                                        <td style={{ padding: '10px 12px' }}>
                                            <span style={{
                                                fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 10,
                                                background: stageBg[d.status] || '#f5f5f5',
                                                color: stageFg[d.status] || '#595959',
                                                border: `1px solid ${stageFg[d.status] || '#d9d9d9'}`,
                                            }}>{d.status}</span>
                                        </td>
                                        <td style={{ padding: '10px 12px' }}>
                                            {/* Mini pipeline dots */}
                                            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                                {STAGES.map((s, i) => (
                                                    <div key={s.key} style={{
                                                        width: 10, height: 10, borderRadius: '50%',
                                                        background: i <= stageIdx
                                                            ? (i === stageIdx ? '#722ed1' : '#52c41a')
                                                            : '#e8e8e8',
                                                    }} />
                                                ))}
                                            </div>
                                        </td>
                                        <td style={{ padding: '10px 12px' }} onClick={e => e.stopPropagation()}>
                                            <div style={{ display: 'flex', gap: 6, flexWrap: 'nowrap' }}>
                                                {/* Next stage action button */}
                                                {stageIdx === 0 && isAdmin && (
                                                    <Tooltip title="HOD Approve">
                                                        <Popconfirm title="Confirm HOD approval?"
                                                            onConfirm={() => handleApproveHOD(d.id)}
                                                            okText="Approve">
                                                            <Button size="small" type="primary" icon={<CheckOutlined />}
                                                                style={{ background: '#1d4ed8', borderColor: '#1d4ed8', fontSize: 11 }}>
                                                                HOD
                                                            </Button>
                                                        </Popconfirm>
                                                    </Tooltip>
                                                )}
                                                {stageIdx === 1 && isAdmin && (
                                                    <Tooltip title="Director Approve">
                                                        <Popconfirm title="Confirm Director approval?"
                                                            onConfirm={() => handleApproveDirector(d.id)}
                                                            okText="Approve">
                                                            <Button size="small" type="primary" icon={<CheckOutlined />}
                                                                style={{ background: '#6d28d9', borderColor: '#6d28d9', fontSize: 11 }}>
                                                                Director
                                                            </Button>
                                                        </Popconfirm>
                                                    </Tooltip>
                                                )}
                                                {stageIdx === 2 && isAdmin && (
                                                    <Tooltip title="Mark as Disposed">
                                                        <Popconfirm title="Confirm physical disposal?"
                                                            onConfirm={() => handleMarkDisposed(d.id)}
                                                            okText="Confirm" okButtonProps={{ danger: true }}>
                                                            <Button size="small" danger icon={<CheckOutlined />} style={{ fontSize: 11 }}>
                                                                Dispose
                                                            </Button>
                                                        </Popconfirm>
                                                    </Tooltip>
                                                )}
                                                {/* Edit — only Pending */}
                                                {stageIdx === 0 && (
                                                    <Tooltip title="Edit">
                                                        <Button size="small" icon={<EditOutlined />}
                                                            onClick={() => { setEditing(d); setFormOpen(true); }} />
                                                    </Tooltip>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Modals */}
            <DisposalFormModal
                open={formOpen} disposal={editing} vehicles={vehicles}
                onSaved={() => { setFormOpen(false); load(); }}
                onClose={() => setFormOpen(false)}
            />
            <DisposalDetail
                disposal={detailItem}
                onClose={() => setDetailItem(null)}
                onEdit={(d) => { setDetailItem(null); setEditing(d); setFormOpen(true); }}
                onApproveHOD={handleApproveHOD}
                onApproveDirector={handleApproveDirector}
                onMarkDisposed={handleMarkDisposed}
                isAdmin={isAdmin}
            />
        </div>
    );
}